import type { EditAreaTemplateApplication, ProductColorOption } from '@repo/api-contracts';
import { type ReactElement, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { useEditAreaDraftStore } from '@/entities/edit-area';
import { EditAreaTemplateActions } from '@/features/edit-area/templates';
import {
  EditAreaLeaveGuard,
  EditAreaToolbar,
  EditAreaValidationSummary,
  GeometryWorkspace,
  MeasurementDriftNotice,
} from '@/features/edit-area-editor';
import { FacePhotoEditor } from '@/features/edit-area-photos';
import { TemplateAppliedNotice } from './template-applied-notice';

interface ProductEditAreaSessionProps {
  readonly productName: string;
  readonly colors: readonly ProductColorOption[];
  /** 적용·복사로 받은 기하. 실측 종류가 이 초안과 같을 때 한 번 넣고 onTemplateConsumed 로 비운다. */
  readonly pendingTemplate: EditAreaTemplateApplication | null;
  readonly onTemplateConsumed: () => void;
  /** 마지막 적용·복사 결과 — 초안에 미저장 변경이 남아 있는 동안만 다음 할 일을 안내한다(저장하면 사라진다). */
  readonly appliedTemplate: {
    readonly application: EditAreaTemplateApplication;
    readonly sourceLabel: string;
  } | null;
  readonly onAppliedDismiss: () => void;
  readonly onTemplateApplied: (
    application: EditAreaTemplateApplication,
    sourceLabel: string,
  ) => void;
}

export const ProductEditAreaSession = ({
  productName,
  colors,
  pendingTemplate,
  onTemplateConsumed,
  appliedTemplate,
  onAppliedDismiss,
  onTemplateApplied,
}: ProductEditAreaSessionProps): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const [photosOpen, setPhotosOpen] = useState(() =>
    state.draft.colors.some((color) => state.draft.activeFaces.some((face) => !color.faces[face])),
  );
  useEffect(() => {
    if (pendingTemplate === null) return;
    if (pendingTemplate.measurementKind !== store.getState().measurementKind) return;
    if (store.getState().applyTemplateGeometry(pendingTemplate.geometry)) setPhotosOpen(true);
    onTemplateConsumed();
  }, [pendingTemplate, store, onTemplateConsumed]);
  return (
    <div className="space-y-5">
      {appliedTemplate !== null && state.dirty ? (
        <TemplateAppliedNotice
          application={appliedTemplate.application}
          sourceLabel={appliedTemplate.sourceLabel}
          onDismiss={onAppliedDismiss}
        />
      ) : null}
      <EditAreaTemplateActions
        productId={state.draft.productId}
        productName={productName}
        hasEditArea={state.serverState !== null}
        hasUnsavedChanges={state.dirty}
        disabled={state.locked}
        onApplied={onTemplateApplied}
      />
      <EditAreaToolbar />
      <MeasurementDriftNotice />
      <EditAreaLeaveGuard />
      <details
        id="edit-area-photos"
        tabIndex={-1}
        className="rounded-lg border p-4"
        open={photosOpen}
        onToggle={(event) => setPhotosOpen(event.currentTarget.open)}
      >
        <summary className="cursor-pointer font-semibold">상품 목업 이미지 관리</summary>
        <section aria-label="상품 목업 이미지" className="mt-4">
          <h2 className="mb-3 text-lg font-semibold">상품 목업 이미지</h2>
          <FacePhotoEditor
            key={state.generation}
            colors={colors}
            managed
            measurementKind={state.measurementKind}
            disabled={state.locked || state.isUnsupported}
            onBeforeFaceRemoval={(face) => {
              const definition = state.faces[face];
              const count = state.draft.colors.filter((color) => color.faces[face]).length;
              const operations = Object.keys(state.operations).filter((key) =>
                key.endsWith(`:${face}`),
              ).length;
              return window.confirm(
                `이 면의 사진 참조 ${count}개, 표면 ${definition?.surfaces.length ?? 0}개, 배치 ${definition?.placements.length ?? 0}개와 진행 작업 ${operations}개를 제거하시겠습니까? 과거 버전과 파일은 보존됩니다.`,
              );
            }}
          />
        </section>
      </details>
      {colors.length === 0 ? (
        <p>먼저 상품 수정 화면에서 색상을 등록해 주세요.</p>
      ) : (
        <GeometryWorkspace key={state.generation} colors={colors} />
      )}
      <EditAreaValidationSummary colors={colors} />
    </div>
  );
};
